import { yupResolver } from "@hookform/resolvers/yup";
import { useCategoriesOptions } from "api/categories/useCategoriesOptions";
import { useCourseGroupAttributesAll } from "api/coursegroup-attributes/useCourseGroupAttributes";
import { CourseGroupAttributeType } from "api/coursegroup-attributes/coursegroupAttributesApi";
import { useCourseGroupsOptions } from "api/hooks/useCourseGroupsOptions";
import { DEFAULT_SECTION_MARGINS, ProductsSectionContent, SectionMargins } from "api/shop";
import { HeadingTagSelect, PaziaFormButton, PaziaInput, PaziaMultiSelect, PaziaSelect } from "components/atoms";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";
import { FormProvider, Resolver, SubmitHandler, useFieldArray, useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

const CARD_DISPLAY_ATTRIBUTE_TYPES = [
  CourseGroupAttributeType.Boolean,
  CourseGroupAttributeType.DictionarySingleSelect,
  CourseGroupAttributeType.DictionaryMultiSelect,
];

const FORM_NAMES = {
  TITLE: "title",
  TITLE_TAG: "titleTag",
  DESCRIPTION: "description",
  CATEGORY_IDS: "categoryIds",
  EXCLUDED_COURSE_GROUP_IDS: "excludedCourseGroupIds",
  FILTERS: "filters",
  CARD_CUSTOMIZATION: "cardCustomization",
} as const;

const FILTER_TYPE_OPTIONS = [
  { value: "category", label: "Kategoria" },
  { value: "attribute", label: "Atrybut" },
];

const DISPLAY_TYPE_OPTIONS = [
  { value: "tabs", label: "Zakładki" },
  { value: "dropdown", label: "Lista rozwijana" },
  { value: "multiselect", label: "Wielokrotny wybór" },
];

const FILTERABLE_ATTRIBUTE_TYPES = [
  CourseGroupAttributeType.Boolean,
  CourseGroupAttributeType.DictionarySingleSelect,
  CourseGroupAttributeType.DictionaryMultiSelect,
];

const filterSchema = yup.object({
  type: yup.string().oneOf(["category", "attribute"]).required(),
  displayType: yup.string().oneOf(["tabs", "dropdown", "multiselect", "switch"]).required(),
  label: yup.string().default(""),
  defaultCategoryIds: yup.array().of(yup.string()).default([]),
  attributeId: yup.number().nullable().default(null),
  attributeName: yup.string().default(""),
});

const schema = yup
  .object({
    [FORM_NAMES.TITLE]: yup.string().required("Tytuł sekcji jest wymagany"),
    [FORM_NAMES.TITLE_TAG]: yup.string(),
    [FORM_NAMES.DESCRIPTION]: yup.string().default(""),
    [FORM_NAMES.CATEGORY_IDS]: yup
      .array()
      .of(yup.string().required())
      .min(1, "Wybierz przynajmniej jedną kategorię")
      .required(),
    [FORM_NAMES.EXCLUDED_COURSE_GROUP_IDS]: yup.array().of(yup.string().required()).default([]),
    [FORM_NAMES.FILTERS]: yup.array().of(filterSchema).default([]),
    [FORM_NAMES.CARD_CUSTOMIZATION]: yup
      .object({
        badgeAttributeIds: yup.array().of(yup.string()).default([]),
        listAttributeIds: yup.array().of(yup.string()).default([]),
      })
      .default({ badgeAttributeIds: [], listAttributeIds: [] }),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface ProductsSectionFormProps {
  onSubmit: (data: ProductsSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: ProductsSectionContent;
  isLoading?: boolean;
}

interface DefaultCategorySelectProps {
  index: number;
  categoryOptions: Array<{ value: string; label: string }>;
}

interface AttributeFilterConfigProps {
  index: number;
  filterableAttributes: Array<{ id: number; name: string; attributeType: CourseGroupAttributeType }>;
  setValue: (name: string, value: any) => void;
}

const AttributeFilterConfig = ({ index, filterableAttributes, setValue }: AttributeFilterConfigProps) => {
  const filterType = useWatch({ name: `${FORM_NAMES.FILTERS}.${index}.type` });

  if (filterType !== "attribute") return null;

  const attributeOptions = filterableAttributes.map((a) => ({
    value: String(a.id),
    label: a.name,
  }));

  const handleAttributeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(e.target.value);
    const attr = filterableAttributes.find((a) => a.id === selectedId);
    if (attr) {
      setValue(`${FORM_NAMES.FILTERS}.${index}.attributeId`, attr.id);
      setValue(`${FORM_NAMES.FILTERS}.${index}.attributeName`, attr.name);
      // Auto-set displayType based on attribute type
      if (attr.attributeType === CourseGroupAttributeType.Boolean) {
        setValue(`${FORM_NAMES.FILTERS}.${index}.displayType`, "switch");
      } else if (attr.attributeType === CourseGroupAttributeType.DictionarySingleSelect) {
        setValue(`${FORM_NAMES.FILTERS}.${index}.displayType`, "dropdown");
      } else if (attr.attributeType === CourseGroupAttributeType.DictionaryMultiSelect) {
        setValue(`${FORM_NAMES.FILTERS}.${index}.displayType`, "multiselect");
      }
    }
  };

  return (
    <div className="mt-3">
      <PaziaSelect
        name={`${FORM_NAMES.FILTERS}.${index}.attributeId`}
        label="Wybierz atrybut"
        options={[{ value: "", label: "— Wybierz —" }, ...attributeOptions]}
        onChange={handleAttributeChange}
      />
    </div>
  );
};

const FilterDisplayTypeField = ({
  index,
  filterableAttributes,
}: {
  index: number;
  filterableAttributes: Array<{ id: number; name: string; attributeType: CourseGroupAttributeType }>;
}) => {
  const filterType = useWatch({ name: `${FORM_NAMES.FILTERS}.${index}.type` });
  const attributeId = useWatch({ name: `${FORM_NAMES.FILTERS}.${index}.attributeId` });

  const attributeType =
    filterType === "attribute"
      ? filterableAttributes.find((a) => a.id === Number(attributeId))?.attributeType
      : undefined;

  const getOptions = () => {
    if (filterType !== "attribute") return DISPLAY_TYPE_OPTIONS;
    switch (attributeType) {
      case CourseGroupAttributeType.Boolean:
        return [{ value: "switch", label: "Przełącznik" }];
      case CourseGroupAttributeType.DictionaryMultiSelect:
        return [
          { value: "multiselect", label: "Wielokrotny wybór" },
          { value: "dropdown", label: "Lista rozwijana" },
        ];
      case CourseGroupAttributeType.DictionarySingleSelect:
        return [
          { value: "dropdown", label: "Lista rozwijana" },
          { value: "tabs", label: "Zakładki" },
        ];
      default:
        return [...DISPLAY_TYPE_OPTIONS, { value: "switch", label: "Przełącznik" }];
    }
  };

  const isDisabled =
    filterType === "attribute" &&
    attributeType !== undefined &&
    attributeType !== CourseGroupAttributeType.DictionarySingleSelect &&
    attributeType !== CourseGroupAttributeType.DictionaryMultiSelect;

  return (
    <PaziaSelect
      name={`${FORM_NAMES.FILTERS}.${index}.displayType`}
      label="Sposób wyświetlania"
      required
      options={getOptions()}
      disabled={isDisabled}
    />
  );
};

const CategoryFilterConfig = ({
  index,
  categoryOptions,
}: {
  index: number;
  categoryOptions: Array<{ value: string; label: string }>;
}) => {
  const filterType = useWatch({ name: `${FORM_NAMES.FILTERS}.${index}.type` });
  if (filterType !== "category") return null;
  return <DefaultCategorySelect index={index} categoryOptions={categoryOptions} />;
};

const DefaultCategorySelect = ({ index, categoryOptions }: DefaultCategorySelectProps) => {
  const displayType = useWatch({
    name: `${FORM_NAMES.FILTERS}.${index}.displayType`,
  });

  if (categoryOptions.length === 0) {
    return (
      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
        Wybierz kategorie powyżej, aby móc ustawić domyślne wartości
      </p>
    );
  }

  if (displayType === "multiselect") {
    return (
      <div className="mt-3">
        <PaziaMultiSelect
          name={`${FORM_NAMES.FILTERS}.${index}.defaultCategoryIds`}
          label="Domyślne kategorie"
          options={categoryOptions}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Wybrane kategorie będą zaznaczone domyślnie</p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <PaziaSelect
        name={`${FORM_NAMES.FILTERS}.${index}.defaultCategoryIds.0`}
        label="Domyślna kategoria"
        options={[{ value: "", label: "Brak" }, ...categoryOptions]}
      />
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Ta kategoria będzie wybrana domyślnie</p>
    </div>
  );
};

interface FormValues {
  title: string;
  titleTag: string;
  description: string;
  categoryIds: string[];
  excludedCourseGroupIds: string[];
  filters: Array<{
    type: "category" | "attribute";
    displayType: "tabs" | "dropdown" | "multiselect" | "switch";
    label: string;
    defaultCategoryIds: string[];
    attributeId: number | null;
    attributeName: string;
  }>;
  cardCustomization: {
    badgeAttributeIds: string[];
    listAttributeIds: string[];
  };
  margins: SectionMargins;
}

export const ProductsSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: ProductsSectionFormProps) => {
  const { options: categoryOptions } = useCategoriesOptions();
  const { allAttributes } = useCourseGroupAttributesAll();

  const filterableAttributes = allAttributes.filter((a) => FILTERABLE_ATTRIBUTE_TYPES.includes(a.attributeType));
  const cardDisplayAttributes = allAttributes.filter((a) => CARD_DISPLAY_ATTRIBUTE_TYPES.includes(a.attributeType));
  const cardDisplayAttributeOptions = cardDisplayAttributes.map((a) => ({ value: String(a.id), label: a.name }));

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      [FORM_NAMES.TITLE]: defaultValues?.title ?? "",
      [FORM_NAMES.TITLE_TAG]: defaultValues?.titleTag ?? "h2",
      [FORM_NAMES.DESCRIPTION]: defaultValues?.description ?? "",
      [FORM_NAMES.CATEGORY_IDS]: defaultValues?.categoryIds?.map(String) ?? [],
      [FORM_NAMES.EXCLUDED_COURSE_GROUP_IDS]: defaultValues?.excludedCourseGroupIds?.map(String) ?? [],
      [FORM_NAMES.FILTERS]: defaultValues?.filters?.map((f) => ({
        type: f.type as "category" | "attribute",
        displayType: f.displayType as "tabs" | "dropdown" | "multiselect" | "switch",
        label: f.label ?? "",
        defaultCategoryIds: f.defaultCategoryIds?.map(String) ?? [],
        attributeId: f.attributeId ?? null,
        attributeName: f.attributeName ?? "",
      })) ?? [
        {
          type: "category" as const,
          displayType: "tabs" as const,
          label: "",
          defaultCategoryIds: [],
          attributeId: null,
          attributeName: "",
        },
      ],
      [FORM_NAMES.CARD_CUSTOMIZATION]: {
        badgeAttributeIds: defaultValues?.cardCustomization?.badgeAttributeIds?.map(String) ?? [],
        listAttributeIds: defaultValues?.cardCustomization?.listAttributeIds?.map(String) ?? [],
      },
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control, formState } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_NAMES.FILTERS,
  });
  console.log("Form errors:", formState.errors);
  // Watch selected categories to filter course groups
  const selectedCategoryIds = useWatch({
    control,
    name: FORM_NAMES.CATEGORY_IDS,
  });

  const { options: courseGroupOptions } = useCourseGroupsOptions(
    false,
    selectedCategoryIds?.map(Number).filter((n) => !isNaN(n)),
  );

  const handleFormSubmit: SubmitHandler<FormValues> = (data) => {
    const formattedData: ProductsSectionFormValues = {
      title: data.title,
      titleTag: data.titleTag as any,
      description: data.description || undefined,
      categoryIds: data.categoryIds.map(Number),
      excludedCourseGroupIds: data.excludedCourseGroupIds.map(Number),
      filters: data.filters.map((f) => ({
        type: f.type,
        displayType: f.displayType,
        label: f.label || undefined,
        defaultCategoryIds:
          f.type === "category" ? f.defaultCategoryIds?.map(Number).filter((n) => !isNaN(n)) : undefined,
        attributeId: f.type === "attribute" ? (f.attributeId ?? undefined) : undefined,
        attributeName: f.type === "attribute" ? f.attributeName || undefined : undefined,
      })),
      cardCustomization: {
        badgeAttributeIds: data.cardCustomization.badgeAttributeIds.map(Number).filter((n) => !isNaN(n)),
        listAttributeIds: data.cardCustomization.listAttributeIds.map(Number).filter((n) => !isNaN(n)),
      },
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaInput name={FORM_NAMES.TITLE} label="Tytuł sekcji" required />
        <HeadingTagSelect name={FORM_NAMES.TITLE_TAG} label="Tag tytułu" />
        <PaziaFromQuill name={FORM_NAMES.DESCRIPTION} label="Opis sekcji" />

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Kategorie</h4>
          <PaziaMultiSelect
            name={FORM_NAMES.CATEGORY_IDS}
            label="Wybierz kategorie"
            required
            options={categoryOptions}
          />
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Wykluczenia</h4>
          <PaziaMultiSelect
            name={FORM_NAMES.EXCLUDED_COURSE_GROUP_IDS}
            label="Wyklucz grupy kursów (opcjonalne)"
            options={courseGroupOptions}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Te grupy kursów nie będą wyświetlane w sekcji produktów
          </p>
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Filtry</h4>
            <button
              type="button"
              onClick={() =>
                append({
                  type: "category",
                  displayType: "tabs",
                  label: "",
                  defaultCategoryIds: [],
                  attributeId: null,
                  attributeName: "",
                })
              }
              className="rounded-lg bg-primary-500 px-3 py-1 text-sm text-white transition-colors hover:bg-primary-600"
            >
              Dodaj filtr
            </button>
          </div>

          {fields.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak filtrów. Kliknij "Dodaj filtr" aby dodać pierwszy filtr.
            </p>
          )}

          {fields.map((field, index) => (
            <div key={field.id} className="mb-3 rounded-lg border border-slate-200 p-4 dark:border-slate-600">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filtr #{index + 1}</span>
                <button type="button" onClick={() => remove(index)} className="text-sm text-red-500 hover:text-red-700">
                  Usuń
                </button>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <PaziaSelect
                  name={`${FORM_NAMES.FILTERS}.${index}.type`}
                  label="Typ filtra"
                  required
                  options={FILTER_TYPE_OPTIONS}
                />
                <FilterDisplayTypeField index={index} filterableAttributes={filterableAttributes} />
                <PaziaInput name={`${FORM_NAMES.FILTERS}.${index}.label`} label="Etykieta (opcjonalna)" />
              </div>
              <AttributeFilterConfig
                index={index}
                filterableAttributes={filterableAttributes}
                // @ts-ignore temporary ignore, we know this is correct
                setValue={methods.setValue}
              />
              <CategoryFilterConfig
                index={index}
                categoryOptions={categoryOptions
                  .filter((opt) => selectedCategoryIds?.includes(String(opt.value)))
                  .map((opt) => ({ value: String(opt.value), label: opt.label }))}
              />
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Personalizacja karty produktu</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <PaziaMultiSelect
              name={`${FORM_NAMES.CARD_CUSTOMIZATION}.badgeAttributeIds`}
              label="Atrybuty dla badge'ów"
              options={cardDisplayAttributeOptions}
            />
            <PaziaMultiSelect
              name={`${FORM_NAMES.CARD_CUSTOMIZATION}.listAttributeIds`}
              label="Atrybuty dla listy"
              options={cardDisplayAttributeOptions}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Wybierz atrybuty (słownikowe lub logiczne), których wartości będą wyświetlane jako badge&apos;e i elementy
            listy na kartach produktów
          </p>
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

export interface ProductsSectionFormValues {
  title: string;
  titleTag?: string;
  description?: string;
  categoryIds: number[];
  excludedCourseGroupIds: number[];
  filters: Array<{
    type: "category" | "attribute";
    displayType: "tabs" | "dropdown" | "multiselect" | "switch";
    label?: string;
    defaultCategoryIds?: number[];
    attributeId?: number;
    attributeName?: string;
  }>;
  cardCustomization?: {
    badgeAttributeIds?: number[];
    listAttributeIds?: number[];
  };
  margins?: SectionMargins;
}
